import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Category } from '../models/category.model';
import { environment } from '../../environments/environment';

const STORAGE_KEY = 'marketplace.categories';

@Injectable({ providedIn: 'root' })
export class MarketplaceCategoryService {
  // Reads use the public taxonomy routes; writes go to api/admin/categories, which only platform
  // staff may call (product brand spec section 7.2).
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/categories';
  private readonly adminUrl = environment.subscriptionApiUrl + '/api/admin/categories';
  private categories$ = new BehaviorSubject<Category[]>(this.load());
  constructor(private http: HttpClient) { }

  getCategories(): Observable<Category[]> {
    // Serve the cached copy immediately, then reconcile with the API.
    this.refresh();
    return this.categories$.asObservable();
  }

  /**
   * Retrieve full details for a single category by id from the API.
   */
  getCategoryById(id: string): Observable<Category> {
    return this.http.get<Category>(`${this.baseUrl}/GetCategoryById/${encodeURIComponent(id)}`);
  }

  addCategory(input: Partial<Category>): Category {
    const now = new Date().toISOString();
    const parentId = input.parentId ?? null;
    const parent = parentId ? this.categories$.value.find(c => c.categoryId === parentId) : undefined;
    let level = input.level !== undefined ? Math.min(Math.max(input.level as number, 0), 2) : undefined as unknown as number;
    if (level === undefined || level === null) {
      level = parent ? Math.min((parent.level ?? 0) + 1, 2) : 0; // 0-2 => 3 levels total
    }

    // Optimistic local object
    const tempId = this.generateId();
    const category: Category = {
      categoryId: tempId,
      categoryName: input.categoryName || 'Untitled',
      categoryDesc: input.categoryDesc || '',
      isActive: !!input.isActive,
      isFeatured: !!input.isFeatured,
      isDeleted: !!input.isDeleted,
      inSeason: !!input.inSeason,
      hideFromMarketplace: !!input.hideFromMarketplace,
      createdAt: now,
      updatedAt: now,
      categoryImg: input.categoryImg || '',
      parentId,
      isEbt: !!input.isEbt,
      taxRate: input.taxRate ?? null,
      createdBy: '',
      updatedBy: '',
      slug: input.slug || '',
      level,
      parent: undefined as unknown as Category,
      childCategories: []
    };
    this.persist([...this.categories$.value, category]);

    // Persist to API and reconcile with server response
    const payload: Partial<Category> = {
      categoryName: category.categoryName,
      categoryDesc: category.categoryDesc,
      isActive: category.isActive,
      isFeatured: category.isFeatured,
      isDeleted: category.isDeleted,
      inSeason: category.inSeason,
      hideFromMarketplace: category.hideFromMarketplace,
      categoryImg: category.categoryImg,
      parentId: category.parentId,
      isEbt: category.isEbt,
      taxRate: category.taxRate,
      slug: category.slug,
      level: category.level
    };
    this.http.post<Category>(this.adminUrl, payload).subscribe({
      next: created => {
        const next = this.categories$.value.map(c => c.categoryId === tempId ? { ...c, ...created } : c);
        this.persist(next);
        // The server has fanned the change out to the other services; re-read so this portal shows
        // the authoritative row (server-assigned id, slug, image URL) rather than the optimistic one.
        this.refresh();
      },
      error: () => {
        // Leave optimistic local state; consider future retry logic
      }
    });

    return category;
  }

  updateCategory(id: string, updates: Partial<Category>): Observable<Category> {
    // Optimistic local update
    const updatedAt = new Date().toISOString();
    const nextLocal = this.categories$.value.map(c => {
      if (c.categoryId !== id) return c;
      const parentId = updates.parentId !== undefined ? updates.parentId : c.parentId;
      const parent = parentId ? this.categories$.value.find(x => x.categoryId === parentId) : undefined;
      let level = updates.level !== undefined ? Math.min(Math.max(updates.level as number, 0), 2) : undefined as unknown as number;
      if (level === undefined || level === null) {
        level = parent ? Math.min((parent.level ?? 0) + 1, 2) : 0;
      }
      return { ...c, ...updates, parentId, level, updatedAt } as Category;
    });
    this.persist(nextLocal);

    // Persist to API and reconcile with server response. categoryId is set from the id argument
    // rather than trusted from `updates`: the API updates WHERE CategoryId = @CategoryId, so a
    // caller that omitted it would silently update no rows and still get a 200 back.
    const payload: Partial<Category> = {
      ...updates,
      categoryId: id,
      parentId: updates.parentId !== undefined ? updates.parentId : nextLocal.find(c => c.categoryId === id)?.parentId,
      level: nextLocal.find(c => c.categoryId === id)?.level
    };
    return this.http.put<Category>(`${this.adminUrl}/${encodeURIComponent(id)}`, payload).pipe(
      tap({
        next: updated => {
          const next = this.categories$.value.map(c => c.categoryId === id ? { ...c, ...updated } : c);
          this.persist(next);
          // Re-read so this portal reflects the saved row rather than the optimistic merge.
          this.refresh();
        },
        error: () => {
          // Keep optimistic local state on error; consider retry
        }
      })
    );
  }

  /**
   * Soft-delete a category (sets isDeleted). There is no Delete endpoint — the API exposes only
   * Create and Update — so this pushes a full update, which also triggers the server-side cache
   * invalidation fan-out. Deleted categories stay in the admin list (the deleted/not-deleted filter
   * relies on that) but drop out of the public marketplace navigation.
   *
   * Returns null when the id is unknown, so callers can distinguish "nothing to do" from a request.
   */
  deleteCategory(id: string): Observable<Category> | null {
    return this.setFlags(id, { isDeleted: true });
  }

  /**
   * Flip a boolean flag by pushing a full update (there is no dedicated toggle endpoint) — the same
   * approach BrandService uses. This previously mutated only local state, so the change looked
   * applied but was never saved and never propagated to the other services.
   */
  toggleFlag(id: string, flag: keyof Pick<Category, 'isActive' | 'isFeatured' | 'isDeleted' | 'hideFromMarketplace'>): Observable<Category> | null {
    const current = this.categories$.value.find(c => c.categoryId === id);
    if (!current) return null;
    return this.setFlags(id, { [flag]: !current[flag] } as Partial<Category>);
  }

  /**
   * Apply flag changes on top of the category's current values and save. The API's update model
   * marks categoryName and slug as required and overwrites every column, so a partial payload would
   * either 400 or blank out fields — always send the whole row.
   */
  private setFlags(id: string, changes: Partial<Category>): Observable<Category> | null {
    const current = this.categories$.value.find(c => c.categoryId === id);
    if (!current) return null;
    return this.updateCategory(id, { ...this.toUpdatePayload(current), ...changes });
  }

  /** The editable fields of a category, as the API's update model expects them. */
  private toUpdatePayload(cat: Category): Partial<Category> {
    return {
      categoryName: cat.categoryName || '',
      categoryDesc: cat.categoryDesc || '',
      categoryImg: cat.categoryImg || '',
      isActive: !!cat.isActive,
      isFeatured: !!cat.isFeatured,
      isDeleted: !!cat.isDeleted,
      inSeason: !!cat.inSeason,
      hideFromMarketplace: !!cat.hideFromMarketplace,
      isEbt: !!cat.isEbt,
      taxRate: cat.taxRate ?? null,
      slug: cat.slug || '',
      level: cat.level,
      parentId: cat.parentId ?? null
    };
  }

  /**
   * Re-fetch the list from the API and replace the cached copy. The localStorage copy has no TTL,
   * so without an explicit refresh after a write this portal could keep showing its own optimistic
   * state indefinitely — even though every other service has already been invalidated server-side.
   */
  refresh(): void {
    this.http.get<Category[]>(`${this.baseUrl}/GetAllCategories`).subscribe({
      next: list => this.persist(list),
      error: () => {
        // Keep existing state on error; the next successful read reconciles.
      }
    });
  }

  /** The currently cached category, without a request. Null when not loaded/unknown. */
  peek(id: string): Category | null {
    return this.categories$.value.find(c => c.categoryId === id) ?? null;
  }

  /** Drop the cached copy entirely (used by the admin cache console). */
  clearCache(): void {
    this.categories$.next([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { }
  }

  private persist(next: Category[]): void {
    this.categories$.next(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch { }
  }

  private load(): Category[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Category[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private generateId(): string {
    return 'cat_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
